import React, { useEffect, useMemo, useImperativeHandle, useRef } from 'react'; 
import { Editor } from '@tinymce/tinymce-react'; 
import { flags } from '../../featureFlags/flags';
import { useFeatureFlag } from '../../featureFlags';
import './styles.less';

// Using this instead of a useState var because setting useState is 
// async and doesn't happen fast enough
let hadUserInput = false ;

const TinyMCE = ( { value, onChange, charLimit = 0, ref } ) => {
    const editorRef = useRef( null ); 
    const showTinyMCE_AI = false; //useFeatureFlag( { name: flags.USE_TINY_MCE_AI } );
    
    /******************************************
     * These functions are called by our parent
     * component when validating the form.
     ******************************************/
    
    useImperativeHandle( ref, () => ( {
        getCharCount() {
            return editorRef.current.plugins.wordcount.body.getCharacterCount();
        },
        getHTMLContent() {
            return editorRef.current.getContent();
        },
        hadUserInput() { 
            return hadUserInput;
        },
        resetHadUserInput() {
            hadUserInput = false;
        }
    } ) );

    /*****************************************
     * If the value prop !== the html content
     * we set initialValue = value. This will
     * trigger the Editor to re-init with this
     * latest value. This only happens on 
     * startup or when a locale changes.
     *****************************************/
    
    const initialValue = useMemo ( () => {
        if ( !editorRef?.current || value !== editorRef.current.getContent() ) {
            return value;
        }
    }, [ value ] );

    /*****************************************
     * onChange is passed from the antd form
     * item. What you pass to this function 
     * will become the form item's value prop.
     *****************************************/

    const handleEditorChange = () => { 
        if ( onChange ) {
            onChange( editorRef.current.getContent() );
            updateCharLimitWarning();
        }
    };
    
    // eslint-disable-next-line no-import-assign
    useEffect( () => {
        hadUserInput = false;

        return () => {
            hadUserInput = false;
        };
    }, [] );

    const handleEditorInit = ( editor ) => {
        editorRef.current = editor; 

        // Trigger click event on wordcount button so it show the char count instead
        window.document.getElementsByClassName( 'tox-statusbar__wordcount' )[ 0 ].click();
        updateCharLimitWarning();
    };

    // Update the wordcount button's color if over charLimit
    const updateCharLimitWarning = () => {

        if ( charLimit && charLimit > 0 ) {
            if ( editorRef.current.plugins.wordcount.body.getCharacterCount() > charLimit ) {
                window.document.getElementsByClassName( 'tox-statusbar__wordcount' )[ 0 ]
                    .classList.add( 'red' );
            } else {
                window.document.getElementsByClassName( 'tox-statusbar__wordcount' )[ 0 ]
                    .classList.remove( 'red' );
            }
        }
    };

    const getPlugins = () => {
        if ( showTinyMCE_AI ) {
            return [
                'advlist', 'ai', 'autolink', 'lists', 'link', 'image', 'charmap', 'nonbreaking',
                'preview', 'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'code', 'wordcount', 'fullscreen',  'tinymcespellchecker'
            ];
        } else {
            return [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'nonbreaking',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'code', 'wordcount', 'fullscreen', 'tinymcespellchecker'
            ];
        }
    };

    const getToolbar = () => {
        if ( showTinyMCE_AI ) {
            return 'aidialog | bold italic underline strikethrough superscript subscript | bullist numlist | link | charmap | code ';
        } else {
            return 'undo redo | link | charmap | nonbreaking | bold italic underline | bullist numlist | outdent indent | searchreplace code | fullscreen';
        }
    };

    /*****************************************
     * Render
     ****************************************/

    return (
        <div className="tiny-mce"> 
            <Editor
                apiKey='j30vyqeapo6td0b22ampdyb1v70y6fuc0v7t3b6zxn7ih0zz'
                onInit={ ( evt, editor ) => handleEditorInit( editor ) }
                initialValue={ initialValue }
                onEditorChange= { handleEditorChange }
                init={ {
                    init_instance_callback: function ( editor ) {
                        editor.on( 'BeforeSetContent', function ( e ) {
                            e.content = e.content.replace( '```html', '' ).replace( '```', '' );
                        } );
                    },
                    height: 300,
                    menubar: false,
                    branding: false,
                    plugins: getPlugins(),
                    toolbar: getToolbar(), 
                    setup: ( editor ) => {
                        editor.on( 'input', ( e ) => { 
                            hadUserInput = true;
                        } );
                    }, 
                    content_style: `body { line-height: 1.75; }`,

                } }
            /> 
        </div>
    );
};

export default TinyMCE;